import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { usePostFormInit } from '../../hooks/usePostFormInit';
import { ICategory } from '../../types/client_add_form';
import { IUser } from '../../types/user';
import {
  cleanupStorage,
  submitAddQuestion,
  submitEditQuestion,
} from '../../reducers/modules/add_question/add_question';
import { uploadImage } from '../../reducers/modules/add_blog/add_blog';
import { isAdminOrModerator } from '../../../app/utils';
import * as Analytics from '../../../app/helpers/analytics_helper';

const CRUMBS = [{ path: '/magazine' }, { path: '/forum-rieltorov' }, { path: '/add-vopros' }];

const SEO = {
  seoTitle: 'Новый вопрос',
  seoUrl: 'https://cian.ru/add-vopros/',
  seoDescription:
    'Циан - форум о недвижимости, советы риелторов и других специалистов недвижимости. Задать вопрос риелтору.',
  seoKeywords: 'недвижимость, форум, советы, вопрос-ответ',
};

const BASE_CATEGORIES: ICategory[] = [
  { id: '', value: '', label: 'Не выбрана' },
  { id: 'city_realty', value: 'city_realty', label: 'Городская недвижимость' },
  { id: 'commerce_realty', value: 'commerce_realty', label: 'Коммерческая недвижимость' },
  { id: 'country_realty', value: 'country_realty', label: 'Загородная недвижимость' },
  { id: 'foreign_realty', value: 'foreign_realty', label: 'Зарубежная недвижимость' },
  { id: 'hypothec', value: 'hypothec', label: 'Ипотека' },
  { id: 'market_trend', value: 'market_trend', label: 'Тенденции рынка' },
];

const CATEGORIES_WITH_SPECIALIST: ICategory[] = [
  ...BASE_CATEGORIES,
  { id: 'specialist', value: 'specialist', label: 'Специалист-специалисту' },
];

const getCategories = (user: IUser): ICategory[] => {
  if (isAdminOrModerator(user) || (user.userId !== -1 && (user.rating || 0) >= 50)) {
    return CATEGORIES_WITH_SPECIALIST;
  }

  return BASE_CATEGORIES;
};

export const useAddQuestionPage = () => {
  const dispatch = useAppDispatch();

  const addQuestion = useAppSelector(state => state.addQuestion);

  const { user, regions, tags, editingItem, deviceType } = usePostFormInit({
    crumbs: CRUMBS,
    seo: SEO,
    postCardEndpoint: '/v1/get-question',
    formState: addQuestion,
    cleanupStorage,
    redirectPathBuilder: (slug, id) => `/forum-rieltorov-${slug}-${id}`,
    fetchUserAfterSubmit: false,
  });

  const onSubmitAdd = (
    content: string,
    status: string,
    isSubscribedToComments: boolean,
    category: string,
    regionsId: number[],
    tags: string,
    title: string,
    userEmail: string,
    userName: string,
  ) => {
    Analytics.addPost('add_question');

    if (isSubscribedToComments) {
      Analytics.subscribeComments('new_comments', 'Magazine_subscription');
    }

    dispatch(
      submitAddQuestion(content, status, isSubscribedToComments, category, regionsId, tags, title, userEmail, userName),
    );
  };

  const onSubmitEdit = (
    id: number,
    content: string,
    status: string,
    isSubscribedToComments: boolean,
    category: string,
    regionsId: number[],
    tags: string,
    title: string,
  ) => {
    dispatch(submitEditQuestion(id, content, status, isSubscribedToComments, category, regionsId, tags, title));
  };

  const onUploadImage = (img: string) => dispatch(uploadImage(img));

  return {
    user,
    regions,
    tags,
    addQuestion,
    editingItem,
    deviceType,
    getCategories: () => getCategories(user),
    onSubmitAdd,
    onSubmitEdit,
    onUploadImage,
  };
};
